// src/utils/rateLimitMessage.js
// One message for every rate-limit refusal (group form addendum 3 item 8; Architect rulings, 4 Oct
// 2026): it names the action and when it can be tried again, e.g. "You can create up to 3 groups a
// day. Try again in about 2 hours." Shown under a notice title (common:rate_limit_title), never
// "Confirmation".
//
// The database's check_rate_limit() refuses with message 'rate_limit_exceeded', HINT = the action,
// and (since 20261004d) DETAIL = JSON {"limit","window_seconds","retry_after_seconds"}. supabaseFetch
// passes the server's whole JSON error body as error.message, so it is parsed here.
//
// Keys are read with an explicit namespace (common:…), so the helper works under any caller's t().

// Each action whose limit has its own sentence, and the window that sentence's wording assumes ("a
// day", "an hour"). If the database ever reports a different window, the generic sentence is used,
// so the text can never be wrong about the window.
const ACTIONS = {
  community_create: { key: 'common:rate_limit_community_create', windowSeconds: 86400 },
  article_create: { key: 'common:rate_limit_article_create', windowSeconds: 3600 },
  chat_create: { key: 'common:rate_limit_chat_create', windowSeconds: 3600 },
  join_request: { key: 'common:rate_limit_join_request', windowSeconds: 3600 }
};

function errorText(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string') return error.message;
  return '';
}

function parseJson(text) {
  try { return JSON.parse(text); } catch (e) { return null; }
}

function positiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

/**
 * What a rate-limit refusal says, or null if the error is not one.
 * @returns {{ action: string|null, limit: number|null, windowSeconds: number|null, retryAfterSeconds: number|null } | null}
 */
export function rateLimitInfo(error) {
  const text = errorText(error);
  if (!text.includes('rate_limit_exceeded')) return null;
  const body = parseJson(text);
  const detail = body && typeof body.details === 'string' ? parseJson(body.details) : null;
  return {
    action: body && typeof body.hint === 'string' ? body.hint : null,
    limit: positiveInt(detail?.limit),
    windowSeconds: positiveInt(detail?.window_seconds),
    retryAfterSeconds: positiveInt(detail?.retry_after_seconds)
  };
}

export function isRateLimited(error) {
  return rateLimitInfo(error) !== null;
}

/**
 * "Try again in about …", always rounded UP, so it never promises a shorter wait than the real one:
 * under an hour in whole minutes (at least 1), otherwise in whole hours.
 */
export function rateLimitWait(retryAfterSeconds, t) {
  const seconds = positiveInt(retryAfterSeconds);
  if (!seconds) return '';
  if (seconds < 3600) {
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    return t(minutes === 1 ? 'common:rate_limit_wait_minutes_one' : 'common:rate_limit_wait_minutes_many', { count: minutes });
  }
  const hours = Math.ceil(seconds / 3600);
  return t(hours === 1 ? 'common:rate_limit_wait_hours_one' : 'common:rate_limit_wait_hours_many', { count: hours });
}

/**
 * The full translated message for a rate-limit refusal; null if the error is not one.
 */
export function rateLimitMessage(error, t) {
  const info = rateLimitInfo(error);
  if (!info) return null;
  const known = info.action ? ACTIONS[info.action] : null;
  const what = known && info.limit && info.windowSeconds === known.windowSeconds
    ? t(`${known.key}_${info.limit === 1 ? 'one' : 'many'}`, { count: info.limit })
    : t('common:rate_limit_generic');
  const when = rateLimitWait(info.retryAfterSeconds, t);
  return when ? `${what} ${when}` : what;
}

export const RATE_LIMIT_TITLE_KEY = 'common:rate_limit_title';
